import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import featureFlag from 'ember-feature-flags/helpers/feature-flag';
import isActive from 'ember-router-helpers/helpers/is-active';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

import NaisHeaderLogo from 'market-view-ui/components/application/nais-header-logo';
import LogoutButton from 'market-view-ui/components/logout-button';
import ActiveDot from 'market-view-ui/components/shared/active-dot';
import trackEvent from 'market-view-ui/helpers/track-event';

import './header-bar.scss';

function ariaCurrent(isRouteActive) {
  return isRouteActive ? 'page' : 'false';
}

const MainNavListItems = class extends Component {
  @service location;

  <template>
    <li>
      <LinkTo
        @route="authenticated.trends"
        aria-current="{{ariaCurrent (isActive 'authenticated.trends')}}"
        ...attributes
        data-test-header-bar-link-trends
      >
        Trends
        {{#if (isActive "authenticated.trends")}}
          <ActiveDot />
        {{/if}}
      </LinkTo>
    </li>
    <li>
      <LinkTo
        @route="authenticated.map"
        @query={{hash
          address=this.location.address
          lat=this.location.lat
          long=this.location.long
        }}
        aria-current="{{ariaCurrent (isActive 'authenticated.map')}}"
        data-test-header-map-link
        ...attributes
      >
        Maps
        {{#if (isActive "authenticated.map")}}
          <ActiveDot />
        {{/if}}
      </LinkTo>
    </li>
    <li>
      {{#if (featureFlag "comparisons")}}
        <LinkTo
          @route="authenticated.comparisons"
          aria-current="{{ariaCurrent (isActive 'authenticated.comparisons')}}"
          data-test-header-bar-link-comparisons
          ...attributes
        >
          Comparisons
          {{#if (isActive "authenticated.comparisons")}}
            <ActiveDot />
          {{/if}}
        </LinkTo>
      {{/if}}
    </li>
  </template>
};

const SecondaryNavListItems = <template>
  <li class="_manage-data">
    <LinkTo
      @route="authenticated.data"
      aria-current="{{ariaCurrent (isActive 'authenticated.data')}}"
      data-test-header-bar-link-data
      ...attributes
    >
      Manage Data
      {{#if (isActive "authenticated.data")}}
        <ActiveDot />
      {{/if}}
    </LinkTo>
  </li>
  <li>
    <a
      href="https://docs.marketview.nais.org/nais"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Help"
      ...attributes
      {{on
        "click"
        (trackEvent "User" "Help Viewed" "from header" isOutbound=true)
      }}
    >
      Knowledge Base
    </a>
  </li>
  <li>
    <a href="#" class="js-intercom-launcher" ...attributes>
      Support Chat
    </a>
  </li>
  <li>
    <a
      href="https://market-view.nolt.io/newest"
      target="_blank"
      rel="noopener noreferrer"
      ...attributes
    >
      Feature Request
    </a>
  </li>
  <li>
    <LinkTo
      @route="about-our-data"
      aria-current="{{ariaCurrent (isActive 'about-our-data')}}"
      ...attributes
    >
      About Our Data
    </LinkTo>
  </li>
  <li>
    <LinkTo
      @route="terms"
      aria-current="{{ariaCurrent (isActive 'terms')}}"
      ...attributes
    >
      Terms of Use
    </LinkTo>
  </li>
</template>;

export default class HeaderBar extends Component {
  @service location;
  @service metrics;

  <template>
    <header class="component-header-bar">
      <NaisHeaderLogo />
      <nav class="_desktop-nav" aria-label="Primary navigation">
        <ul>
          <MainNavListItems class="_nav-link" />
          <li class="_more">
            <details>
              {{! The rule is wrong }}
              {{! template-lint-disable no-nested-interactive }}
              <summary data-test-header-bar-nav-menu-toggle>
                More
                {{svgJar "heroicons-chevron-down" aria-hidden="true"}}
              </summary>
              <ul class="_dropdown-menu">
                <SecondaryNavListItems />
                <li>
                  <LogoutButton>
                    Log out
                  </LogoutButton>
                </li>
              </ul>
            </details>
          </li>
        </ul>
      </nav>

      <nav class="_mobile-nav" aria-label="Primary navigation">
        <details>
          <summary data-test-header-bar-mobile-nav-menu-toggle>
            {{svgJar
              "heroicons-bar-3"
              aria-label="Open Nav"
              class="_open-icon"
            }}
            {{svgJar
              "heroicons-x-mark"
              aria-label="Close Nav"
              class="_close-icon"
            }}
          </summary>
          <ul class="_dropdown-menu">
            <MainNavListItems class="_main-nav-item" />
            <SecondaryNavListItems class="_secondary-nav-item" />
            <li class="_logout-button">
              <LogoutButton>
                Log out
              </LogoutButton>
            </li>
          </ul>
        </details>
      </nav>
    </header>
  </template>
}
